import * as React from 'react';
import {
  Stack,
  Text,
  DefaultButton,
  PrimaryButton,
  DocumentCard,
  DocumentCardDetails,
} from '@fluentui/react';
import type { IDocumentCardStyles } from '@fluentui/react';

import type { ICRMCase } from '../../../Interfaces/ICRMCase';
import type { PBTFlag } from '../../../Interfaces/IPBTCase';
import { FlagBadge } from '../../../Components/PBT/FlagBadge/FlagBadge';
import { crmPageStyles, caseCardStyles, compactButtonStyles } from './CRMCasesPage.styled';

interface ICRMCaseCardProps {
  crmCase: ICRMCase;
  flags: PBTFlag[];
  canCreateCase: boolean;
  onEnterDetails: (c: ICRMCase) => void;
}

const cardStyles: IDocumentCardStyles = caseCardStyles;

function CRMCaseCard({ crmCase: c, flags, canCreateCase, onEnterDetails }: ICRMCaseCardProps): React.ReactElement {
  return (
    // Fluent DocumentCard hardcodes role="group" on its root, which is not a valid child of the
    // role="list" grid (WCAG 1.3.1 / aria-required-children). Wrap it in a rendered role="listitem"
    // element (styled as the grid item) so the list semantics don't depend on display:contents.
    <div role="listitem" className={crmPageStyles.caseListItem}>
      <DocumentCard styles={cardStyles}>
        <DocumentCardDetails>
        <Stack className={crmPageStyles.caseCardHeader}>
          <Stack horizontal horizontalAlign="space-between" verticalAlign="center">
            <Text as="h2" className={crmPageStyles.employeeName}>{c.employeeName}</Text>
            {c.isDuplicate && <span className={crmPageStyles.duplicateBadge}>Duplicate</span>}
          </Stack>
          <Text className={crmPageStyles.caseSubtitle}>{c.crmCaseId} · PERN {c.personnelNumber}</Text>
        </Stack>

        <Stack className={crmPageStyles.caseCardBody} tokens={{ childrenGap: 8 }}>
          <Stack horizontal tokens={{ childrenGap: 24 }}>
            <Stack>
              <Text className={crmPageStyles.label}>Manager</Text>
              <Text className={crmPageStyles.value}>{c.managerName || '—'}</Text>
            </Stack>
            <Stack>
              <Text className={crmPageStyles.label}>Country</Text>
              <Text className={crmPageStyles.value}>{c.country}</Text>
            </Stack>
          </Stack>
          <Stack>
            <Text className={crmPageStyles.label}>LPM Status</Text>
            <Text className={crmPageStyles.value}>{c.lpmStatus}</Text>
          </Stack>
          <Stack>
            <Text className={crmPageStyles.label}>Created in CRM</Text>
            <Text className={crmPageStyles.value}>{new Date(c.createdInCrm).toLocaleDateString()}</Text>
          </Stack>
          {flags.length > 0 && (
            <Stack>
              <Text className={crmPageStyles.label}>Flags</Text>
              <FlagBadge flags={flags} />
            </Stack>
          )}
        </Stack>

        {canCreateCase && (
          <Stack className={crmPageStyles.caseCardFooter} horizontal tokens={{ childrenGap: 8 }}>
            <PrimaryButton
              text="Enter Details"
              onClick={() => onEnterDetails(c)}
              disabled={c.isDuplicate}
              styles={compactButtonStyles}
            />
            {c.isDuplicate && <DefaultButton text="View Existing" styles={compactButtonStyles} disabled />}
          </Stack>
        )}
      </DocumentCardDetails>
      </DocumentCard>
    </div>
  );
}

export { CRMCaseCard };
